import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowLeft, Books, Calendar, ListNumbers, ArrowRight } from "@phosphor-icons/react";

export default function SeriesSingle() {
  const { username, slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get(`/sites/${username}/series/${slug}`);
        setData(res);
      } catch (e) {
        setErr("Series not found or private.");
      }
    })();
  }, [username, slug]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <h1 className="font-serif text-3xl font-black mb-4">{err}</h1>
      <Link to="/explore" className="link-underline">Back to Explore</Link>
    </div>
  );

  if (!data) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-muted-foreground animate-pulse">
      Loading series...
    </div>
  );

  const { series, author, articles } = data;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 lg:py-20">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        {/* Header Section */}
        <div className="col-span-12 lg:col-span-5">
          <Link to={`/${username}`} className="link-underline text-xs eyebrow text-muted-foreground mb-6 inline-flex items-center gap-2">
            <ArrowLeft size={12} /> Back to {author.name || author.username}
          </Link>
          
          <div className="aspect-[16/10] bg-secondary border border-border overflow-hidden mb-8 shadow-2xl">
            {series.cover_image ? (
              <img src={fileUrl(series.cover_image)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center opacity-20"><Books size={80} weight="thin" /></div>
            )}
          </div>

          <div className="eyebrow text-orange-600 font-black mb-3 tracking-[0.2em] flex items-center gap-2">
            <ListNumbers size={16} /> SERIES COLLECTIVE
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-6">
            {series.title}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed italic mb-8 border-l-4 border-border pl-6">
            {series.description || "A collection of interconnected thoughts and stories."}
          </p>

          <div className="flex items-center gap-4 py-6 border-t border-border">
            {author.profile_picture ? (
              <img src={fileUrl(author.profile_picture)} alt="" className="h-12 w-12 rounded-full object-cover shadow-lg" />
            ) : (
              <div className="h-12 w-12 bg-secondary rounded-full grid place-items-center font-bold text-xl">{(author.name || "A")[0]}</div>
            )}
            <div>
              <div className="eyebrow text-[10px] text-muted-foreground">Written by</div>
              <Link to={`/${username}`} className="font-serif text-lg font-bold hover:underline">{author.name || author.username}</Link>
            </div>
          </div>
        </div>

        {/* Article List Section */}
        <div className="col-span-12 lg:col-span-7">
          <div className="eyebrow text-muted-foreground mb-6 flex items-center justify-between border-b border-border pb-3">
            <span>Playlist Content ({articles.length} pieces)</span>
            <span className="font-mono text-[10px]">v1.0</span>
          </div>

          <div className="space-y-6">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link 
                  to={`/${username}/${a.slug}`}
                  className="group flex gap-6 p-4 border border-border hover:bg-card hover:border-foreground transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-foreground text-background font-serif text-2xl font-black grid place-items-center group-hover:scale-110 transition-transform">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                       <span className="eyebrow text-[9px] text-muted-foreground flex items-center gap-1">
                        <Calendar size={10} /> {new Date(a.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-[9px] eyebrow text-muted-foreground">·</span>
                      <span className="text-[9px] eyebrow text-muted-foreground">{a.reading_time || 1}m read</span>
                    </div>
                    <h2 className="font-serif text-xl font-bold group-hover:underline underline-offset-4 decoration-1">{a.title}</h2>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
                      {a.excerpt || "No summary available for this chapter."}
                    </p>
                  </div>
                  <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight size={20} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-secondary border border-border text-center">
            <h3 className="font-serif text-lg font-bold mb-2">Want to stay updated?</h3>
            <p className="text-xs text-muted-foreground mb-6">Subscribe to {author.name}'s newsletter to get new chapters in your inbox.</p>
            <Button asChild className="rounded-none px-8">
              <Link to={`/${username}`}>Visit Author Site</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Button({ children, className, asChild, ...props }) {
  const Comp = asChild ? "span" : "button";
  return (
    <Comp className={`inline-flex items-center justify-center bg-foreground text-background font-bold py-2 px-4 transition-colors hover:bg-foreground/90 ${className}`} {...props}>
      {children}
    </Comp>
  );
}
